import React, { useState, memo } from 'react';
import PropTypes from 'prop-types';

import './style.css';
import cx from "classnames";

var CollapsibleField = function(props) {
  var field = props.field;
  var onFieldClick = props.onFieldClick;
  var onDragStart = props.onDragStart;
  var onBulkAdd = props.onBulkAdd;
  var disableDrag = props.disableDrag;
  var useDoubleClick = props.useDoubleClick;
  var counter = props.counter;
  var recordsFieldUniquenessIdentifier = props.recordsFieldUniquenessIdentifier;
  var showAddButton = props.showAddButton;
  var isLast = props.isLast;
  var ancestorIsLast = props.ancestorIsLast || []; // Track which ancestors are last in their group
  var stickyTopOffset = props.stickyTopOffset || 0; // Offset for section headers

  // Controlled expand/collapse from parent (RuleFields windowing)
  var expandedGroups = props.expandedGroups;
  var onToggleGroup = props.onToggleGroup;
  var controlled = expandedGroups !== undefined && onToggleGroup !== undefined;

  // Optional: Set of visible suuids for search filtering (passed from RuleFields)
  var visibleSuuids = props.visibleSuuids;

  // Fallback to internal state when not controlled
  var _open = useState(true);
  var internalOpen = _open[0];
  var setInternalOpen = _open[1];

  var open = controlled ? (expandedGroups.has(field.suuid)) : internalOpen;

  var fieldCounter = counter || 1;
  var depth = fieldCounter - 1;
  var hasChildren = field.children && field.children.length > 0;
  var isGroup = hasChildren && field.type === 'group';
  var isDescription = field.type === 'description';

  var isShow = function() {
    if (field.suuid === recordsFieldUniquenessIdentifier) return false;
    // When visibleSuuids is provided, use it for visibility
    if (visibleSuuids) return visibleSuuids.has(field.suuid);
    if (field.isShown) return true;
    if (!field.children) return false;
    var show = false;
    field.children.forEach(function(item) {
      if (item.isShown) { show = true; }
    });
    return show;
  };

  var handleToggle = function(e) {
    if (e) {
      e.stopPropagation();
    }
    if (controlled) {
      onToggleGroup(field.suuid);
    } else {
      setInternalOpen(function(prev) { return !prev; });
    }
  };

  var handleFieldClick = function(e) {
    // In column selection mode (drag enabled), groups and description fields cannot be selected
    // In rule selection mode (drag disabled), groups and description fields CAN be selected
    if (!disableDrag && (isGroup || isDescription)) {
      // Apply error shake via direct DOM manipulation to avoid React re-render
      // which disrupts scroll position in the virtualized list
      var el = e.currentTarget;
      el.classList.add('field-collapsible-data--error');
      setTimeout(function() {
        el.classList.remove('field-collapsible-data--error');
      }, 500);
      return;
    }
    // Allow selecting the field if not disabled
    if (!field.disable) {
      onFieldClick(field);
    }
  };

  // Handle mousedown to detect drag attempts on groups or description fields
  // Only applies in column selection mode (drag enabled)
  var handleMouseDown = function(e) {
    // Only trigger error flash for groups or description fields when user starts dragging motion
    // Skip this in rule selection mode (drag disabled) since groups/descriptions are selectable there
    if (!disableDrag && (isGroup || isDescription) && !field.disable) {
      var target = e.currentTarget;

      // Save scroll position before preventDefault (which can disrupt scroll)
      var scrollEl = target.closest('.field-selector-content');
      var savedScroll = scrollEl ? scrollEl.scrollTop : 0;

      // preventDefault on mousedown is the only reliable way to block text selection.
      e.preventDefault();

      // Guard scroll position for the entire mousedown-to-mouseup duration.
      // preventDefault on mousedown can cause async scroll resets at unpredictable
      // times, so we listen for scroll events and immediately restore.
      var scrollGuard = function() {
        if (scrollEl && scrollEl.scrollTop !== savedScroll) {
          scrollEl.scrollTop = savedScroll;
        }
      };
      if (scrollEl) {
        scrollEl.scrollTop = savedScroll;
        scrollEl.addEventListener('scroll', scrollGuard);
      }

      var startX = e.clientX;
      var startY = e.clientY;
      var triggered = false;

      var handleMouseMove = function(moveEvent) {
        // If user moves mouse more than 5px, they're trying to drag
        var deltaX = Math.abs(moveEvent.clientX - startX);
        var deltaY = Math.abs(moveEvent.clientY - startY);
        if (!triggered && (deltaX > 5 || deltaY > 5)) {
          triggered = true;
          // Apply error shake via direct DOM manipulation to avoid React re-render
          // which disrupts scroll position in the virtualized list
          target.classList.add('field-collapsible-data--error');
          setTimeout(function() {
            target.classList.remove('field-collapsible-data--error');
          }, 400);
        }
      };

      var handleMouseUp = function() {
        if (scrollEl) {
          scrollEl.removeEventListener('scroll', scrollGuard);
        }
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };

      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    }
  };

  var handleAddClick = function(e) {
    e.stopPropagation();
    if (!field.disable && onFieldClick) {
      onFieldClick(field);
    }
  };

  var handleBulkAddClick = function(e) {
    e.stopPropagation();
    if (onBulkAdd) {
      onBulkAdd(field);
    }
  };

  var handleDragStart = function(e) {
    if (field.disable || isGroup || isDescription || disableDrag) {
      e.preventDefault();
      return;
    }
    // Extract only the properties needed for column creation (avoid circular refs)
    // Only include primitive values - parent might be an object reference
    var fieldData = {
      sourceCuuid: field.sourceCuuid || field.cuuid,
      cuuid: field.cuuid,
      title: field.title,
      suuid: field.suuid,
      fieldAttribute: field.fieldAttribute,
      type: field.type,
      meta: field.meta,
      recordEntryAttribute: field.recordEntryAttribute,
    };
    // Only include parent if it's a string (suuid reference), not an object
    if (typeof field.parent === 'string') {
      fieldData.parent = field.parent;
    }
    e.dataTransfer.setData('application/json', JSON.stringify(fieldData));
    e.dataTransfer.effectAllowed = 'copy';
    if (onDragStart) {
      onDragStart(field);
    }
  };

  // Filter visible children
  var visibleChildren;
  if (hasChildren) {
    if (visibleSuuids) {
      // Use visibleSuuids set for filtering
      visibleChildren = field.children.filter(function(child) {
        if (child.suuid === recordsFieldUniquenessIdentifier) return false;
        return visibleSuuids.has(child.suuid);
      });
    } else {
      visibleChildren = field.children.filter(function(child) {
        if (child.suuid === recordsFieldUniquenessIdentifier) return false;
        if (child.isShown) return true;
        if (!child.children) return false;
        var show = false;
        child.children.forEach(function(item) {
          if (item.isShown) { show = true; }
        });
        return show;
      });
    }
  } else {
    visibleChildren = [];
  }

  return isShow() ? (
    <div className={cx('field-collapsible', {
      'field-collapsible-disable': field.disable,
      'field-collapsible-selected': field.alreadySelected,
      'field-collapsible-has-children': hasChildren,
      'field-collapsible-is-last': isLast,
    })}>
      <div
        className={cx('field-collapsible-container', {
          'field-collapsible-sticky': hasChildren && open
        })}
        style={hasChildren && open ? { top: (depth * 38) + stickyTopOffset - 6, zIndex: 100 - depth } : undefined}
      >
        {/* Tree connector lines for nested items (depth 1+) */}
        {depth > 0 && (
          <div className="field-tree-lines">
            {Array.from({ length: depth - 1 }).map(function(_, i) {
              // Hide vertical line if the ancestor at this level was the last item
              var hideThisLine = ancestorIsLast[i + 1];
              return (
                <span key={i} className={cx('field-tree-line', {
                  'field-tree-line-hidden': hideThisLine
                })} />
              );
            })}
            <span className={cx('field-tree-branch', {
              'field-tree-branch-last': isLast
            })} />
          </div>
        )}

        {/* Expand/collapse toggle button */}
        {hasChildren && (
          <button
            type="button"
            className={cx('field-collapsible-toggle', { 'field-collapsible-toggle-open': open })}
            onClick={handleToggle}
          >
            <span className="material-symbols-outlined">chevron_right</span>
          </button>
        )}

        {/* Spacer for leaf nodes to align with parent nodes that have toggles */}
        {!hasChildren && (
          <span className="field-collapsible-spacer" />
        )}

        {/* Field data */}
        <div
          className={cx('field-collapsible-data', {
            'field-collapsible-data--no-drag': disableDrag
          })}
          onClick={useDoubleClick ? undefined : handleFieldClick}
          onDoubleClick={useDoubleClick ? handleFieldClick : undefined}
          onMouseDown={handleMouseDown}
          draggable={!field.disable && !isGroup && !isDescription && !disableDrag}
          onDragStart={handleDragStart}
        >
          <div className="field-collapsible-title">{field.title}</div>
          <div className={cx('field-collapsible-type', {
            'field-collapsible-type-section': hasChildren
          })}>
            {field.type}
          </div>
          {showAddButton && !field.disable && !isGroup && !isDescription && !(hasChildren && onBulkAdd) && (
            <button
              type="button"
              className="field-collapsible-add-btn"
              onClick={handleAddClick}
              title="Add field"
            >
              <span className="material-symbols-outlined">add</span>
            </button>
          )}
        </div>

        {/* Bulk add button for group fields */}
        {hasChildren && onBulkAdd && (
          <button
            type="button"
            className="field-collapsible-bulk-add"
            onClick={handleBulkAddClick}
            title="Add all fields"
          >
            <span className="material-symbols-outlined">bolt</span>
          </button>
        )}
      </div>

      {/* Children */}
      {open && hasChildren && (
        <div className="field-collapsible-children">
          {visibleChildren.map(function(child, index) {
            var childIsLast = index === visibleChildren.length - 1;
            // Pass down ancestor info: add current item's isLast status to the array
            var newAncestorIsLast = ancestorIsLast.concat([isLast]);
            return (
              <CollapsibleField
                field={child}
                key={child.suuid || child.recordEntryAttribute}
                onFieldClick={onFieldClick}
                onDragStart={onDragStart}
                onBulkAdd={onBulkAdd}
                disableDrag={disableDrag}
                useDoubleClick={useDoubleClick}
                showAddButton={showAddButton}
                counter={fieldCounter + 1}
                recordsFieldUniquenessIdentifier={recordsFieldUniquenessIdentifier}
                isLast={childIsLast}
                ancestorIsLast={newAncestorIsLast}
                stickyTopOffset={stickyTopOffset}
                expandedGroups={expandedGroups}
                onToggleGroup={onToggleGroup}
                visibleSuuids={visibleSuuids}
              />
            );
          })}
        </div>
      )}
    </div>
  ) : null;
};

CollapsibleField.propTypes = {
  field: PropTypes.object.isRequired,
  onFieldClick: PropTypes.func.isRequired,
  onDragStart: PropTypes.func,
  onBulkAdd: PropTypes.func,
  disableDrag: PropTypes.bool,
  useDoubleClick: PropTypes.bool,
  showAddButton: PropTypes.bool,
  counter: PropTypes.number,
  stickyTopOffset: PropTypes.number,
  isLast: PropTypes.bool,
  ancestorIsLast: PropTypes.array,
  expandedGroups: PropTypes.object,
  onToggleGroup: PropTypes.func,
  visibleSuuids: PropTypes.object,
};

export default memo(CollapsibleField);
